import Vue from 'vue';
import { MAX_SESSION_LENGTH_MS } from "../../../constants_calendar";
// import api from "../../../api/cofocusAPI";

const defaultCurrentSessionData = {
  rematch: true,
  worktype: "really good work"
};

const getDefaultState = function() {
  return {
    /**
     * keys: dateTimeMS {Number}
     * values: {
     *  @SID {String} session's ID
     *  @partnerID {String} partner's ID
     *  @partnerSessionData {Object} partner's public session data 
     *  @currentSessionData {Object} current user's public session data 
     * }
     * public session data includes the following:
     *  rematchAllowed 
     *  workType (also stored in tasksModule, but here for convenience)
     *  // in the future it may also include session length, tasks for this session etc 
     */

    upcomingMatches: {},
    // TODO: add a sorted array of upcoming match dateTimes
    nextMatchDateTimeMS: -1,

    minutesToNextSession: null,
  };
};

const state = getDefaultState();

const getters = {
  /**
   * if no next match, nextMatchData == null. Otherwise 
   * {
   *  SID: {String} -- sessionId
   *  dateTimeMS: {Number} -- session date
   *  partner: {
   *    metadata: {Object}
   *    preferences: {Array}
   *    session: {Object} // partner session data
   *  }
   * }
   */
  nextMatchData: (state, getters, rootState) => { // eslint disable-line no-unused-vars
    if (state.nextMatchDateTimeMS > 0 && state.upcomingMatches[state.nextMatchDateTimeMS]) {
      const nextMatch = state.upcomingMatches[state.nextMatchDateTimeMS];
      const partner = nextMatch.partnerID ? rootState.calendarUsers.users[nextMatch.partnerID] : null;
      return {
        SID: nextMatch.SID,
        dateTimeMS: state.nextMatchDateTimeMS,
        partner: partner ? {
          metadata: partner.metadata,
          preferences: partner.preferences,
          session: nextMatch.partnerSessionData
        } : null,
        currentUserJoined: nextMatch.currentSessionData && nextMatch.currentSessionData.joined
      };
    }
    return null;
  },
  getMatchForDateTime: (state) => (dateTimeMS) => state.upcomingMatches[dateTimeMS], 
};

const mutations = {
  /**
   * @param {
   *  dateTimeMS,
   *  SID
   *  partnerID,
   *  partnerSessionData,
   *  currentSessionData
   * } payload 
   * 
   */
  ADD_UPCOMING_MATCH(state, payload) {
    // console.log("adding upcoming match...")
    Vue.set(state.upcomingMatches, payload.dateTimeMS, {
      SID: payload.SID,
      partnerID: payload.partnerID,
      partnerSessionData: payload.partnerSessionData,
      currentSessionData: payload.currentSessionData
    });
  },
  /**
   * Replaces the partner at dateTimeMS with the new partner. 
   * Assumes that the upcoming match at dateTimeMS exists.
   * @param {
   *  dateTimeMS,
   *  SID, 
   *  partnerID, 
   *  partnerSessionData,
   * } payload
   */
  SET_UPCOMING_MATCH_PARTNER(state, payload) {
    Object.assign(state.upcomingMatches[payload.dateTimeMS], {
      SID: payload.SID,
      partnerID: payload.partnerID,
      partnerSessionData: payload.partnerSessionData,
    });
  },

  /**
   * Assumes that the properties dateTimeMS and field exist.
   * @param {
   *  dateTimeMS -- the datetime for which to set the field
   *  field -- field to update 
   *  newValue -- the new value to add
   * }
   */
  UPDATE_UPCOMING_MATCH_FIELD(state, payload) {
    state[payload.dateTimeMS][payload.field] = payload.newValue;
  },
  /**
   * @param {Number} dateTimeMS date which to delete
   */
  DELETE_UPCOMING_MATCH(state, dateTimeMS) {
    Vue.delete(state.upcomingMatches, dateTimeMS);
  },
  UPDATE_NEXT_MATCH_DATETIMEMS(state, newDateTimeMS) {
    state.nextMatchDateTimeMS = newDateTimeMS;
  },

  SET_MINUTES_TO_SESSION(state, newMinutes) {
    state.minutesToNextSession = newMinutes;
  },
  SET_USER_AS_JOINED_DURING_SESSION(state, matchDateTimeMS) {
    const match = state.upcomingMatches[matchDateTimeMS];
    if (!match.currentSessionData) {
      Vue.set(state.upcomingMatches[matchDateTimeMS], "currentSessionData", { joined: true });
    } else {
      match.currentSessionData.joined = true;
    }
  }
};

const actions = {
  /**
   * Sets an upcoming match
   * @param {
   *  dateTimeMS: {Number},
   *  SID: {String},
   *  partnerID: {String}, 
   *  partnerSessionData: {Object},
   *  currentSessionData: {Object},
   * } payload 
   */
  setUpcomingMatch({state, commit}, payload) {
    commit("ADD_UPCOMING_MATCH", payload); 
    if (Number(payload.dateTimeMS) < state.nextMatchDateTimeMS || state.nextMatchDateTimeMS < 0) {
      commit("UPDATE_NEXT_MATCH_DATETIMEMS", Number(payload.dateTimeMS));
    }
  },
  /**
   * Sets the partner, if the match does not exist, creates the match.
   * @param {
   *  dateTimeMS,
   *  SID,
   *  partnerID,
   *  partnerSessionData,
   * } payload 
   */
  setUpcomingMatchPartner({commit}, payload) {
    if (!state.upcomingMatches[payload.dateTimeMS]) {
      /* if match does not exist, add the match (should never happen) */
      payload.currentSessionData = defaultCurrentSessionData; // TODO: 
      commit("ADD_UPCOMING_MATCH", payload);
      if (Number(payload.dateTimeMS) < state.nextMatchDateTimeMS || state.nextMatchDateTimeMS < 0) {
        commit("UPDATE_NEXT_MATCH_DATETIMEMS", Number(payload.dateTimeMS));
      }
    } else {
      commit("SET_UPCOMING_MATCH_PARTNER", payload);
    }
  },
  /**
   * Removes the partner data from the given datetime. 
   * That is, sets partnerID and partnerSessionData to null
   * @param {Number} dateTimeMS the dateTime for which to remove the match
   */
  removeUpcomingMatchPartner({state, commit}, dateTimeMS) {
    if (state.upcomingMatches[dateTimeMS]) {
      const { SID } = state.upcomingMatches[dateTimeMS];
      commit("SET_UPCOMING_MATCH_PARTNER", {
        dateTimeMS,
        SID, 
        partnerID: null,
        partnerSessionData: null
      });
    }
  },
  /**
   * Adds the data requested from DB in bulk.
   * @param {
   *  metadata: {Object} partner's metadata
   *  preferences: {Object} partner's preferences 
   *  sessions: [
   *    {
   *      SID: {String} session's ID 
   *      d: {Number} session's dateTime in MS 
   *      current: {Object} current user's session data (worktype, rematch)
   *      partner: {Object} partner's public session data (worktype, rematch)
   *    }, 
   *    { ... } 
   *  ]
   * } upcomingSessions
   * @param {Number} dataStart
   * @param {Number} dataEnd
   */
  async addFromDB({state, commit, dispatch}, payload) {
    const {upcomingSessions, dataStart, dataEnd} = payload;
    /* add users to the calendarUsers */
    dispatch("calendarUsers/setUserArray", Object.values(upcomingSessions).map(x => { return {
      metadata: x.metadata, preferences: x.preferences}}), {root: true});
    
    /* to delete nonexistent datetimes */  
    const insertedDateTimes = {};
    
    Object.keys(upcomingSessions).forEach(partnerID => {
      const sessions = upcomingSessions[partnerID].sessions;
      for (let i=0; i<sessions.length; i++) {
        commit("ADD_UPCOMING_MATCH", {
          dateTimeMS: Number(sessions[i].d),
          SID: sessions[i].SID,
          partnerID,
          partnerSessionData: sessions[i].partner,
          currentSessionData: sessions[i].current
        });
        insertedDateTimes[Number(sessions[i].d)] = true;
      }
    });

    /* collect all nonexisting datetimes within the updated range */
    Object.keys(state.upcomingMatches).forEach(dateTimeMS => {
      if (dateTimeMS >= dataStart && dateTimeMS <= dataEnd) {
        if (!insertedDateTimes[dateTimeMS]) {
          insertedDateTimes[dateTimeMS] = false;
        }
      }
    });
    await Promise.all(Object.keys(insertedDateTimes).filter(x => !insertedDateTimes[x]).map(dateTimeMS => {
      return dispatch("deleteUpcomingMatch", dateTimeMS);
    }));

    /* update next match */
    const upcomingDateTimes = Object.keys(state.upcomingMatches);
    let minDate = -1;
    for (let i=0; i<upcomingDateTimes.length; i++) {
      if (Number(upcomingDateTimes[i]) < minDate || minDate < 0) {
        minDate = Number(upcomingDateTimes[i]);
      }
    }
    commit("UPDATE_NEXT_MATCH_DATETIMEMS", minDate);
    /* selectabilities are updated when the request is made */
    return true;
  },

  /**
   * Deletes upcoming match and updates the next match datetime. 
   * Will also update slot selectability.
   * @param {Number} dateTimeMS the datetime to remove
   */
  deleteUpcomingMatch({state, commit, dispatch}, dateTimeMS) {
    commit("DELETE_UPCOMING_MATCH", dateTimeMS);
    let sortedUpcomingDateTimes = Object.keys(state.upcomingMatches).map(x=>Number(x)).sort((a,b)=> a-b);
    console.log("@deleteUpcomingMatch: datetimes", sortedUpcomingDateTimes);
    if (dateTimeMS === state.nextMatchDateTimeMS) {
      /* update the new next match datetime */
      console.log("delete! datetimes: ", sortedUpcomingDateTimes);
      if (sortedUpcomingDateTimes.length === 0) commit("UPDATE_NEXT_MATCH_DATETIMEMS", -1);
      else commit("UPDATE_NEXT_MATCH_DATETIMEMS", sortedUpcomingDateTimes[0]);  
    }
    dispatch("calendar/updateAvailabilitiesAfterDeletion", {
      dateTimesMS: sortedUpcomingDateTimes,
      deletedMS: Number(dateTimeMS)
    }, {root: true});
  },
  
  /**
   * Returns the datetimes of upcoming matches that cover at least one slot within the given view. 
   * @param {
   *  viewStartMS {Number},
   *  viewEndMS {Number}
   * }
   * @return {Array} the datetimes of upcoming matches that cover one slot within the view 
   */
  getMatchesInRange({state}, ranges) {
    const { viewStartMS, viewEndMS } = ranges; 
    const dates = Object.keys(state.upcomingMatches).filter(dateTimeMS => {
      return viewStartMS-MAX_SESSION_LENGTH_MS <= dateTimeMS && dateTimeMS <= viewEndMS+MAX_SESSION_LENGTH_MS; 
    }).map(dateTimeMS => Number(dateTimeMS)).sort((a,b) => a-b);
    return dates;
  },

  setMinutesToNextSession({state, commit}, newMinutes) {
    if (state.nextMatchDateTimeMS > 0) commit("SET_MINUTES_TO_SESSION", newMinutes);
    else commit("SET_MINUTES_TO_SESSION", null);
  },

  setUserAsJoined({state, commit}) {
    console.log("@setUserAsJoined store: ", state.nextMatchDateTimeMS, Date.now());
    if (state.nextMatchDateTimeMS > 0 && state.nextMatchDateTimeMS < Date.now()) {
      commit("SET_USER_AS_JOINED_DURING_SESSION", state.nextMatchDateTimeMS);
    }
  },

  /**
   * @param {String} userID the current user's ID
   * @param {Object | null} pastMatch the payload contains a completed past match from DB. If the pastMatch is null, 
   * it is checked whether the current next match has turned to a past match 
   * and if so, the current next match is completed 
   * Contains fields:
   * @param {Array} UIDs the UIds 
   * @param {Object | null} user1_sessionData the first user's sessionData
   * @param {Object | null} user2_sessionData 
   * @param {Date} dateTime  
   * This function is a fucking spachett
   */
  moveToPastMatches({state, dispatch}, payload) {
    const {pastMatch, userID} = payload;
    if (pastMatch !== null) {
      console.log("@move To Past Matches the past match: ", pastMatch);
      let partnerID = null, sessionData = pastMatch.user1_sessionData;
      if (pastMatch.UIDs.length >= 2) {
        const partnerIndex = pastMatch.UIDs[0] === userID ? 1 : 0;
        partnerID = pastMatch.UIDs[partnerIndex];
        sessionData = partnerIndex === 0 ? pastMatch.user2_sessionData : pastMatch.user1_sessionData;
      }
      const pastMatchData = {
        SID: pastMatch._id,
        dateTimeMS: (new Date(pastMatch.dateTime).valueOf()),
        partnerID,
        sessionData
      };
      console.log("the past match data: ", pastMatchData);
      dispatch("deleteUpcomingMatch", (new Date(pastMatch.dateTime)).valueOf());
      dispatch("pastMatches/addPastMatch", pastMatchData, { root: true });
    } else {
      console.log("@move to past Matches PAST WAS NULL", Date.now() - MAX_SESSION_LENGTH_MS, state.nextMatchDateTimeMS);
      if (state.nextMatchDateTimeMS < Date.now() - MAX_SESSION_LENGTH_MS) {
        console.log("moving next to past!");
        const match = state.upcomingMatches[state.nextMatchDateTimeMS];
        if (match) {
          const pastMatchData = {
            SID: match.SID,
            dateTimeMS: state.nextMatchDateTimeMS,
            partnerID: match.partnerID,
            sessionData: match.currentSessionData
          };
          console.log("the past match data: ", pastMatchData);
          dispatch("deleteUpcomingMatch", state.nextMatchDateTimeMS);
          dispatch("pastMatches/addPastMatch", pastMatchData, { root: true });
        }
      }
    }
  }

  // /**
  //  * Requests from DB the next session and updates 
  //  */
  // async updateNextSession({commit}, userID) {
  //   if (!userID) return;
  //   try {
  //     const result = await api.REQUEST({
  //       method: "get",
  //       url: `api/users/${userID}/next-session`,
  //       withCredentials: true
  //     });
  //     if (result && result.data && result.data.success) {
  //       /* request was successful */
  //       commit("UPDATE_NEXT_MATCH", result.data.nextSession);
  //     } 
  //   } catch (error) {
  //     console.log("@updateNextSession error: ", error);
  //   }
  //   // why not lol
  //   return true;
  // }
};

const upcomingMatches = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default upcomingMatches;

